
import React from 'react';
import { Clock, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Article, formatDate } from '@/data/articles';
import CategoryBadge from './CategoryBadge';
import { Link } from 'react-router-dom';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'minimal';
  className?: string;
  imageHeight?: string;
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  variant = 'default',
  className,
  imageHeight = 'h-48',
}) => {
  const {
    id,
    title,
    excerpt,
    author,
    category,
    publishedAt,
    readTime,
    imageUrl,
    trending,
    priority
  } = article;

  const variants = {
    default: "flex flex-col h-full",
    horizontal: "flex flex-col md:flex-row md:items-center gap-6 h-full",
    minimal: "flex flex-col border-0 p-0 h-full"
  };

  const imageClasses = {
    default: "w-full rounded-lg overflow-hidden mb-4",
    horizontal: "w-full md:w-2/5 rounded-lg overflow-hidden",
    minimal: "w-full rounded-lg overflow-hidden mb-3"
  };

  const contentClasses = {
    default: "flex-1 flex flex-col",
    horizontal: "flex-1 flex flex-col",
    minimal: "flex-1 flex flex-col"
  };

  return (
    <article 
      className={cn(
        "group article-card-transition rounded-xl hover-scale h-full bg-white/5 backdrop-blur-sm hover:bg-white/10 shadow-sm",
        variants[variant],
        className
      )}
    >
      <div className={cn(imageClasses[variant])}>
        <div className={cn("relative w-full bg-muted", imageHeight)}>
          <div className="relative w-full h-full">
            <img 
              src={imageUrl} 
              alt={title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/placeholder.svg';
              }}
            />
          </div>
          <div className="absolute top-3 right-3 flex gap-2">
            {trending && (
              <div className="bg-red-600 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md">
                Trending
              </div>
            )}
            {priority === 'high' && (
              <div className="bg-amber-500 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md flex items-center gap-1">
                <Star className="w-3 h-3" />
                <span>Priority</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className={cn(contentClasses[variant], "p-3 sm:p-4")}>
        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-2.5">
          <CategoryBadge categoryId={category} size="sm" />
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{readTime} min read</span>
          </div>
        </div>
        
        <Link to={`/articles/${id}`} className="block">
          <h3 className="text-base sm:text-lg md:text-xl font-semibold leading-tight mb-1.5 sm:mb-2 transition-colors duration-200 group-hover:text-primary/80 line-clamp-2">
            {title}
          </h3>
        </Link>
        
        <p className={cn(
          "text-muted-foreground text-xs sm:text-sm mb-2 sm:mb-3 flex-1",
          variant === 'minimal' ? 'line-clamp-2' : 'line-clamp-3'
        )}>
          {excerpt}
        </p>
        
        <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-100/10">
          <div className="flex items-center gap-2">
            <img 
              src={author.avatar} 
              alt={author.name}
              className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover"
            />
            <span className="text-xs font-medium truncate max-w-[100px] sm:max-w-[140px]">{author.name}</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {formatDate(publishedAt)}
          </span>
        </div>
      </div>
    </article>
  );
};

export default ArticleCard;
